// ============================================
// src/app/models/product.model.ts
// ============================================
import type { ActivityTypeInfo } from './cluster.model';

// 🔹 Modèle de produit

export interface Product {
  id: string;
  name: string;
  description: string;
  category: string;
  purchasePrice: number; // Prix d'achat utilisateur
  sellPrice: number; // Prix de vente utilisateur
  quantity: number; // Stock de référence (setup du marchand)
  quantityAvailable?: number; // Stock disponible pour le service en cours
  unit: string; // Unité (kg, L, pièce, etc.)
  sku: string; // Code produit
  image?: string; // URL de l'image
  productIcon?: string;
  isRecommended?: boolean; // depuis backend
  isCustom?: boolean; // vrai si ajouté par l'utilisateur
  isAISuggested?: boolean; // suggéré par l'IA
  popularity?: number; // depuis backend
  isNew?: boolean; // depuis backend
  riskType?: 'rupture' | 'surstock' | null; // Type de risque détecté

  // Seuils de stock personnalisés (présets par défaut, ajustables)
  minStockThreshold?: number; // Seuil bas - alerte rupture si stock < ce seuil
  maxStockThreshold?: number; // Seuil haut - alerte surstock si stock > ce seuil

  createdAt: Date;
  updatedAt: Date;

  // ✅ Champs provenant du catalogue
  defaultPurchasePrice?: number;
  defaultSellPrice?: number;
  defaultQuantity?: number;
  // activityType défini ci-dessous

  // ✅ Activation / Saisonnalité
  isActive?: boolean; // true par défaut, false si désactivé
  deactivationReason?: 'seasonal' | 'weather' | 'manual' | 'stock'; // raison de désactivation
  seasonalMonths?: number[]; // mois de disponibilité (1-12)
  lastActivatedAt?: Date; // dernière activation
  lastDeactivatedAt?: Date; // dernière désactivation
  activityType?: string; // activityType du marchand

  // 🔗 Lien vers le CatalogProduct source (optionnel)
  sourceCatalogProductId?: string | null;

  // 🏷️ Source du produit
  sourceType?: 'core' | 'extended' | 'custom';

  // 🧊 Périssabilité (high = jour même, medium = 1-2j, low = longue conservation)
  perishability?: 'high' | 'medium' | 'low';
  // ⏱️ Durée de conservation indicative
  shelfLife?: string;
}

// 🔹 Recherche / filtres
export interface ProductSearchFilters {
  searchTerm?: string;
  category?: string;
  isNew?: boolean;
}

// 🔹 Catégories
export interface ProductCategory {
  name: string;
  icon: string;
}

// 🔹 Metadonnées dynamiques récupérées depuis le backend
export interface ProductMetadata {
  categories: ProductCategory[];
  units: string[];
  activityTypes: ActivityTypeInfo[];
}

// ============================================
// 🔹 SEUILS DE STOCK PAR CLUSTER
// ============================================

/**
 * Configuration des seuils de stock pour détection des risques
 */
export interface StockThresholds {
  minStock: number; // Seuil bas (risque rupture)
  maxStock: number; // Seuil haut (risque surstock)
}

/**
 * Configuration des seuils par catégorie de produit
 */
export interface CategoryThresholds {
  [category: string]: StockThresholds;
}

/**
 * Préférences de seuils de stock du marchand
 * Permet la personnalisation des seuils par défaut
 */
export interface MerchantStockPreferences {
  useCustomThresholds: boolean; // Si true, utilise les seuils personnalisés
  globalThresholds?: StockThresholds; // Seuils globaux personnalisés
  categoryThresholds?: CategoryThresholds; // Seuils par catégorie personnalisés
}

/**
 * Seuils de stock par cluster/type de marchand
 * Valeurs pré-configurées basées sur les règles métier
 * Ces seuils servent de valeurs par défaut pour les produits
 *
 * Les 9 types d'activité :
 * - bakery, sandwich_shop, food_truck_burger, food_truck_world,
 * - food_truck_resto, bar_cafe, ice_cream, fresh_produce, market_deli
 */
export const CLUSTER_STOCK_THRESHOLDS: Record<string, CategoryThresholds> = {
  // =========================================
  // BOULANGERIE-PÂTISSERIE
  // =========================================
  bakery: {
    Boulangerie: { minStock: 5, maxStock: 200 },
    Pains: { minStock: 5, maxStock: 200 },
    Baguettes: { minStock: 5, maxStock: 200 },
    Viennoiserie: { minStock: 5, maxStock: 150 },
    Viennoiseries: { minStock: 5, maxStock: 150 },
    Pâtisserie: { minStock: 3, maxStock: 100 },
    Pâtisseries: { minStock: 3, maxStock: 100 },
    Sandwichs: { minStock: 3, maxStock: 80 },
    Traiteur: { minStock: 3, maxStock: 60 },
    Snacking: { minStock: 3, maxStock: 60 },
    Boissons: { minStock: 5, maxStock: 100 },
    default: { minStock: 3, maxStock: 100 },
  },

  // =========================================
  // SANDWICHERIE / SNACKING
  // =========================================
  sandwich_shop: {
    Sandwichs: { minStock: 5, maxStock: 80 },
    Paninis: { minStock: 3, maxStock: 60 },
    Salades: { minStock: 3, maxStock: 50 },
    Wraps: { minStock: 3, maxStock: 50 },
    'Snacking chaud': { minStock: 3, maxStock: 50 },
    Formules: { minStock: 5, maxStock: 80 },
    Boissons: { minStock: 5, maxStock: 100 },
    Desserts: { minStock: 3, maxStock: 60 },
    default: { minStock: 3, maxStock: 80 },
  },

  // =========================================
  // FOOD TRUCK BURGER / AMÉRICAIN
  // =========================================
  food_truck_burger: {
    Burgers: { minStock: 5, maxStock: 100 },
    Plats: { minStock: 3, maxStock: 60 },
    Accompagnements: { minStock: 5, maxStock: 120 },
    Frites: { minStock: 5, maxStock: 120 },
    Formules: { minStock: 3, maxStock: 80 },
    Boissons: { minStock: 5, maxStock: 100 },
    Sauces: { minStock: 5, maxStock: 100 },
    Desserts: { minStock: 3, maxStock: 50 },
    default: { minStock: 3, maxStock: 80 },
  },

  // =========================================
  // FOOD TRUCK CUISINE DU MONDE
  // =========================================
  food_truck_world: {
    Tacos: { minStock: 5, maxStock: 80 },
    Nems: { minStock: 5, maxStock: 60 },
    Bobun: { minStock: 5, maxStock: 60 },
    Accompagnements: { minStock: 5, maxStock: 80 },
    Boissons: { minStock: 5, maxStock: 100 },
    Sauces: { minStock: 5, maxStock: 80 },
    default: { minStock: 5, maxStock: 80 },
  },

  // =========================================
  // FOOD TRUCK RESTAURATION
  // =========================================
  food_truck_resto: {
    Plats: { minStock: 5, maxStock: 80 },
    Burgers: { minStock: 5, maxStock: 80 },
    Sandwichs: { minStock: 3, maxStock: 60 },
    Tacos: { minStock: 3, maxStock: 60 },
    Wraps: { minStock: 3, maxStock: 60 },
    Accompagnements: { minStock: 5, maxStock: 80 },
    Frites: { minStock: 5, maxStock: 80 },
    Formules: { minStock: 3, maxStock: 60 },
    Boissons: { minStock: 5, maxStock: 100 },
    Desserts: { minStock: 3, maxStock: 40 },
    Sauces: { minStock: 5, maxStock: 80 },
    default: { minStock: 3, maxStock: 80 },
  },

  // =========================================
  // BAR / CAFÉ / SALON DE THÉ
  // =========================================
  bar_cafe: {
    Cafés: { minStock: 5, maxStock: 200 },
    Thés: { minStock: 5, maxStock: 150 },
    'Boissons chaudes': { minStock: 5, maxStock: 150 },
    'Boissons froides': { minStock: 5, maxStock: 100 },
    Bières: { minStock: 5, maxStock: 120 },
    Vins: { minStock: 3, maxStock: 80 },
    Apéritifs: { minStock: 3, maxStock: 60 },
    Alcools: { minStock: 3, maxStock: 100 },
    Viennoiserie: { minStock: 3, maxStock: 60 },
    'Petit-déjeuner': { minStock: 3, maxStock: 40 },
    Plats: { minStock: 3, maxStock: 50 },
    Formules: { minStock: 3, maxStock: 50 },
    Snacks: { minStock: 3, maxStock: 60 },
    Pâtisserie: { minStock: 3, maxStock: 60 },
    Brunch: { minStock: 3, maxStock: 40 },
    default: { minStock: 3, maxStock: 80 },
  },

  // =========================================
  // GLACIER / FROZEN
  // =========================================
  ice_cream: {
    Glaces: { minStock: 5, maxStock: 150 },
    Sorbets: { minStock: 5, maxStock: 120 },
    Coupes: { minStock: 3, maxStock: 100 },
    Gaufres: { minStock: 3, maxStock: 80 },
    Crêpes: { minStock: 3, maxStock: 60 },
    Cornets: { minStock: 5, maxStock: 200 },
    Toppings: { minStock: 3, maxStock: 80 },
    Boissons: { minStock: 5, maxStock: 100 },
    default: { minStock: 3, maxStock: 100 },
  },

  // =========================================
  // PRIMEUR / PRODUITS FRAIS
  // =========================================
  fresh_produce: {
    Fruits: { minStock: 5, maxStock: 100 },
    Légumes: { minStock: 5, maxStock: 100 },
    Herbes: { minStock: 3, maxStock: 50 },
    Fromages: { minStock: 3, maxStock: 40 },
    Charcuterie: { minStock: 3, maxStock: 30 },
    'Produits laitiers': { minStock: 3, maxStock: 60 },
    Transformés: { minStock: 3, maxStock: 50 },
    Épicerie: { minStock: 3, maxStock: 60 },
    default: { minStock: 3, maxStock: 80 },
  },

  // =========================================
  // TRAITEUR MARCHÉ
  // =========================================
  market_deli: {
    Charcuterie: { minStock: 3, maxStock: 50 },
    Plats: { minStock: 3, maxStock: 40 },
    'Plats préparés': { minStock: 3, maxStock: 40 },
    Salades: { minStock: 3, maxStock: 40 },
    Entrées: { minStock: 3, maxStock: 40 },
    Fromages: { minStock: 3, maxStock: 30 },
    Accompagnements: { minStock: 3, maxStock: 40 },
    Desserts: { minStock: 3, maxStock: 30 },
    Boissons: { minStock: 5, maxStock: 60 },
    default: { minStock: 3, maxStock: 50 },
  },
};

/**
 * Seuils par défaut si le cluster n'est pas trouvé
 */
export const DEFAULT_STOCK_THRESHOLDS: StockThresholds = {
  minStock: 3,
  maxStock: 100,
};

/**
 * Récupère les seuils par défaut pour un cluster/catégorie
 * @param activityType - Type d'activité du marchand
 * @param category - Catégorie du produit
 */
export function getDefaultStockThresholds(
  activityType: string,
  category: string,
): StockThresholds {
  const clusterThresholds = CLUSTER_STOCK_THRESHOLDS[activityType];
  if (clusterThresholds) {
    return (
      clusterThresholds[category] ||
      clusterThresholds['default'] ||
      DEFAULT_STOCK_THRESHOLDS
    );
  }
  return DEFAULT_STOCK_THRESHOLDS;
}

/**
 * Récupère les seuils de stock pour un produit donné
 * Priorité: seuils du produit > seuils cluster > seuils par défaut
 */
export function getProductStockThresholds(
  product: Product,
  activityType: string,
): StockThresholds {
  // Si le produit a ses propres seuils, les utiliser
  if (
    product.minStockThreshold !== undefined &&
    product.maxStockThreshold !== undefined
  ) {
    return {
      minStock: product.minStockThreshold,
      maxStock: product.maxStockThreshold,
    };
  }

  // Sinon utiliser les seuils par défaut du cluster
  return getDefaultStockThresholds(activityType, product.category || 'default');
}
